import {
  Box,
  IconButton,
  Paper,
  Skeleton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import { useCallback, useEffect, useReducer, useState } from 'react';
import { useLocation } from 'react-router-dom';
import deleteIcon from '../../../../../../assets/delete-red.png';
import downloadIcon from '../../../../../../assets/download.svg';
import { ReactComponent as gifIcon } from '../../../../../../assets/gif.svg';
import { ReactComponent as jpegIcon } from '../../../../../../assets/jpeg.svg';
import { ReactComponent as jpgIcon } from '../../../../../../assets/jpg.svg';
import { ReactComponent as mp4Icon } from '../../../../../../assets/mp4.svg';
import { ReactComponent as pdfIcon } from '../../../../../../assets/pdf.svg';
import renameIcon from '../../../../../../assets/pen.svg';
import { ReactComponent as pngIcon } from '../../../../../../assets/png.svg';
import { ReactComponent as svgIcon } from '../../../../../../assets/svg.svg';
import { ReactComponent as webpIcon } from '../../../../../../assets/webp.svg';
import { CompleteOperationType, StartOperationType } from '../../../../../../model/alerts.model';
import { Column } from '../../../../../../model/modal.model';
import { modalReducer } from '../../../../../../reducers/modalReducer';
import { DocumentsService } from '../../../../../../service/documents.service';
import zeplinColor from '../../../../../../theme/Color';
import { Utilities } from '../../../../../../utilities/utilities';
import CustomButton from '../../../../../shared/CustomButton/CustomButton';
import CustomDeleteModal from '../../../../../shared/CustomDeleteModal/CustomDeleteModal';
import { LightTooltip } from '../../../../../shared/CustomTooltip/CustomTooltip';
import { DragDropModal } from './components/DragDropModal';
import { NoDocuments } from './components/NoDocuments';
import RenameModal from './components/RenameModal';
import styles from './DocumentsTable.module.css';

const fileIconComponents = {
  mp4: mp4Icon,
  pdf: pdfIcon,
  png: pngIcon,
  jpg: jpgIcon,
  jpeg: jpegIcon,
  gif: gifIcon,
  webp: webpIcon,
  svg: svgIcon,
};

const columns: readonly Column[] = [
  { id: 'Name', label: 'Name' },
  { id: 'updated', label: 'Updated' },
  {
    id: 'size',
    label: 'Size',
    align: 'center',
  },
  {
    id: 'actions',
    label: '',
    align: 'center',
  },
];

export type DocumentType = {
  fileExtension: string;
  fileName: string;
  fileSize: number;
  s3Bucket: string;
  mimeType: string;
  fileId: string;
  uploadedBy: string;
  clientId: string;
  s3Key: string;
  uploadedAt: string;
};

export const DocumentsTable = ({
  startOperation,
  completeOperation,
}: {
  startOperation: StartOperationType;
  completeOperation: CompleteOperationType;
}) => {
  const client = useLocation();
  const clientId = client.state?.client.AccountId;
  const [files, setFiles] = useState<DocumentType[]>([]);
  const [selectedFile, setSelectedFile] = useState<DocumentType | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [modalState, modalDispatch] = useReducer(modalReducer, {
    delete: false,
    rename: false,
    upload: false,
  });

  const fetchDocuments = useCallback(async () => {
    setLoading(true);
    try {
      const data = await DocumentsService.getDocuments(clientId);
      setFiles(data.files);
    } catch (error) {
      console.error('Error fetching documents:', error);
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  useEffect(() => {
    fetchDocuments();
  }, [fetchDocuments]);

  const handleDeleteFile = async () => {
    if (selectedFile === null) return;
    modalDispatch({ type: 'CLOSE', payload: 'delete' });

    startOperation('delete', 'Deleting file...');
    try {
      await DocumentsService.deleteDocument(clientId, selectedFile?.fileId);
      await fetchDocuments();
      setSelectedFile(null);
      completeOperation('delete', 'Deletion completed');
    } catch (error) {
      console.error('Error deleting file:', error);
      completeOperation('delete', 'Failed to delete file.', true);
    }
  };

  const handleDownloadFile = async (fileId: string) => {
    startOperation('download', 'Downloading...');
    try {
      const response = await DocumentsService.downloadDocument(clientId, fileId);
      const { downloadUrl } = response;
      const link = document.createElement('a');
      link.href = downloadUrl;
      link.click();
      completeOperation('download', 'Download completed');
    } catch (error) {
      completeOperation('download', 'Failed to start download.', true);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {[0, 1, 2].map(() => (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '16px',
              gap: '16px',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: '16px' }}>
              <Skeleton variant='rounded' width='40px' height='40px' />
              <Skeleton variant='rounded' width='157px' height='19px' />
            </Box>
            <Skeleton variant='rounded' width='178px' height='17px' />
            <Skeleton variant='rounded' width='46px' height='17px' />
            <Box sx={{ display: 'flex', gap: '10px' }}>
              <Skeleton variant='rounded' width='10px' height='10px' />
              <Skeleton variant='rounded' width='10px' height='10px' />
            </Box>
          </Box>
        ))}
      </Box>
    );
  }

  if (!files.length) {
    return (
      <NoDocuments
        fetchDocuments={fetchDocuments}
        startOperation={startOperation}
        completeOperation={completeOperation}
        isModalOpen={modalState.upload}
        modalDispatch={modalDispatch}
      />
    );
  }

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant='responsiveHeading3'>All Files</Typography>
          <Box sx={{ display: 'flex', gap: '16px' }}>
            <LightTooltip
              title={
                <Typography variant='responsiveBody3' color={zeplinColor.Neutral1}>
                  Add File
                </Typography>
              }
              placement='bottom-end'
              style={{ backgroundColor: zeplinColor.BasicBlack, borderRadius: '8px' }}
            >
              <Box sx={{ width: '100%' }}>
                <CustomButton
                  disabled={false}
                  onClick={() => modalDispatch({ type: 'OPEN', payload: 'upload' })}
                  variant='contained'
                  color={'primary'}
                  buttonVariant='responsiveButtonLarge'
                  style={{
                    width: '100%',
                    borderRadius: { xs: '10px', xl: '14px' },
                    height: { xs: 30, xl: 48 },
                    padding: { xs: '10px 12px', xl: '14px 16px' },
                    '&.Mui-focusVisible': { backgroundColor: '#333CAA' },
                  }}
                  textStyle={{ color: zeplinColor.Neutral1 }}
                  text={'Add file'}
                  icon={
                    <img
                      src={`/assets/add-white.svg`}
                      className={styles['add-icon']}
                      alt='plus icon'
                    />
                  }
                />
              </Box>
            </LightTooltip>
          </Box>
        </Box>
        <Paper sx={{ width: '100%', border: 'none', boxShadow: 'none' }}>
          <TableContainer
            sx={{
              overflow: 'scroll',
              height: { xs: 'calc(100vh - 390px)', xl: 'calc(100vh - 450px)' },
            }}
          >
            <Table stickyHeader aria-label='sticky table'>
              <TableHead>
                <TableRow
                  sx={{
                    border: '1px solid black',
                    borderRadius: '14px',
                    '& th': {
                      backgroundColor: '#f5f5f5',
                    },
                    '& th:first-child': {
                      borderRadius: '14px 0px 0px 14px',
                    },
                    '& th:last-child': {
                      borderRadius: '0px 14px 14px 0px',
                    },
                  }}
                >
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={column.align}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Typography variant='responsiveBody3'>{column.label}</Typography>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {files.map((item) => {
                  return (
                    <TableRow
                      role='checkbox'
                      key={item.fileId}
                      hover
                      sx={{
                        border: '3px blue',
                        '&:hover': {
                          borderTop: '1px solid red',
                          borderBottom: '1px solid red',
                          cursor: 'pointer',
                        },
                      }}
                    >
                      <TableCell component='th' scope='row'>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {(() => {
                            const fileType = item.fileExtension;
                            const IconComponent =
                              fileIconComponents[fileType as keyof typeof fileIconComponents];
                            return (
                              <IconComponent
                                aria-label='file icon'
                                style={{ width: '40px', height: '40px' }}
                              />
                            );
                          })()}
                          <Typography
                            sx={{ marginLeft: '20px', fontSize: '16px' }}
                            variant='responsiveBody2'
                          >
                            {item.fileName}.{item.fileExtension}
                          </Typography>
                          <IconButton
                            sx={{ width: '32px', height: '32px' }}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedFile(item);
                              modalDispatch({ type: 'OPEN', payload: 'rename' });
                            }}
                          >
                            <img
                              src={renameIcon}
                              alt='rename icon'
                              width={'20px'}
                              height={'20px'}
                            />
                          </IconButton>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant='responsiveBody3'>
                          {Utilities.formatDateAndAuthor({
                            date: item.uploadedAt,
                            author: item.uploadedBy,
                          })}
                        </Typography>
                      </TableCell>

                      <TableCell align='center'>
                        <Typography variant='responsiveBody3'>
                          {Utilities.formatBytesToMB(item.fileSize)} MB
                        </Typography>
                      </TableCell>

                      <TableCell align='right'>
                        <IconButton
                          sx={{ width: '32px', height: '32px', marginRight: '8px' }}
                          onClick={async (e) => {
                            e.stopPropagation();
                            setSelectedFile(item);
                            handleDownloadFile(item.fileId);
                          }}
                        >
                          <img
                            src={downloadIcon}
                            alt='download icon'
                            width={'20px'}
                            height={'20px'}
                          />
                        </IconButton>
                        <IconButton
                          sx={{ width: '32px', height: '32px' }}
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedFile(item);
                            modalDispatch({ type: 'OPEN', payload: 'delete' });
                          }}
                        >
                          <img src={deleteIcon} alt='delete icon' width={'20px'} height={'20px'} />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  );
                })}
                <RenameModal
                  isModalOpen={modalState.rename}
                  modalDispatch={modalDispatch}
                  fileName={selectedFile?.fileName || ''}
                  fileId={selectedFile?.fileId || ''}
                  fetchDocuments={fetchDocuments}
                  startOperation={startOperation}
                  completeOperation={completeOperation}
                />
                <CustomDeleteModal
                  isModalOpen={modalState.delete}
                  modalDispatch={modalDispatch}
                  onDelete={handleDeleteFile}
                  title='Delete File'
                  description='Are you sure you want to delete this file?'
                />
                <DragDropModal
                  isModalOpen={modalState.upload}
                  modalDispatch={modalDispatch}
                  fetchDocuments={fetchDocuments}
                  startOperation={startOperation}
                  completeOperation={completeOperation}
                />
              </TableBody>
            </Table>
          </TableContainer>
        </Paper>
      </Box>
    </>
  );
};
